import React, { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useClickOutside from '../../../Hooks/useClickOutside';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminHeaderAction,
  AdminTableHeader,
  AdminTableBody,
  AdminLinkAction,
  AdminRowMenuItem,
  AdminRowActionsButton,
  AdminSupportingText,
  AdminDetailFieldLabel,
  AdminStatusBadge,
  AdminFormSecondaryAction,
  adminFormDetailInputClasses,
  adminFormDetailSelectClasses,
  adminFormDetailTextareaClasses,
  AdminRowMenuIcon,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_TABLE_CLASS,
  ADMIN_FORM_PANEL_CLASS,
  ADMIN_FORM_FOOTER_CLASS,
} from '../shared/adminUx';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';

// Permission levels (Document 2 — Role Detail module access)
const PERMISSION_LEVELS = { FULL: 'Full', VIEW: 'View Only', LIMITED: 'Limited', NOT_ALLOWED: 'No Access' };

const PERMISSION_MODULES = [
  'Dashboard',
  'Customers',
  'Businesses',
  'Posts',
  'Reviews',
  'Messages & Quotes',
  'Moderation',
  'Compliance',
  'Fraud & Security',
  'Support',
  'Finance',
  'Reports Hub',
  'Website Content',
  'Team Members',
  'Settings',
];

const ALL_MODULES_LABEL = 'All Modules';

// Recommended role matrix (Document 2 §9) — roleId -> module -> access level
const PERMISSION_MATRIX = {
  coOwner: {
    Dashboard: PERMISSION_LEVELS.FULL,
    Customers: PERMISSION_LEVELS.FULL,
    Businesses: PERMISSION_LEVELS.FULL,
    Posts: PERMISSION_LEVELS.FULL,
    Reviews: PERMISSION_LEVELS.FULL,
    'Messages & Quotes': PERMISSION_LEVELS.FULL,
    Moderation: PERMISSION_LEVELS.FULL,
    Compliance: PERMISSION_LEVELS.FULL,
    'Fraud & Security': PERMISSION_LEVELS.FULL,
    Support: PERMISSION_LEVELS.FULL,
    Finance: PERMISSION_LEVELS.FULL,
    'Reports Hub': PERMISSION_LEVELS.FULL,
    'Website Content': PERMISSION_LEVELS.FULL,
    'Team Members': PERMISSION_LEVELS.LIMITED,
    Settings: PERMISSION_LEVELS.LIMITED,
  },
  operationsAdmin: {
    Dashboard: PERMISSION_LEVELS.FULL,
    Customers: PERMISSION_LEVELS.FULL,
    Businesses: PERMISSION_LEVELS.FULL,
    Posts: PERMISSION_LEVELS.VIEW,
    Reviews: PERMISSION_LEVELS.VIEW,
    'Messages & Quotes': PERMISSION_LEVELS.VIEW,
    Moderation: PERMISSION_LEVELS.VIEW,
    Compliance: PERMISSION_LEVELS.VIEW,
    'Fraud & Security': PERMISSION_LEVELS.NOT_ALLOWED,
    Support: PERMISSION_LEVELS.VIEW,
    Finance: PERMISSION_LEVELS.LIMITED,
    'Reports Hub': PERMISSION_LEVELS.VIEW,
    'Website Content': PERMISSION_LEVELS.NOT_ALLOWED,
    'Team Members': PERMISSION_LEVELS.NOT_ALLOWED,
    Settings: PERMISSION_LEVELS.LIMITED,
  },
  technicalSupport: {
    Dashboard: PERMISSION_LEVELS.VIEW,
    Customers: PERMISSION_LEVELS.NOT_ALLOWED,
    Businesses: PERMISSION_LEVELS.NOT_ALLOWED,
    Posts: PERMISSION_LEVELS.NOT_ALLOWED,
    Reviews: PERMISSION_LEVELS.NOT_ALLOWED,
    'Messages & Quotes': PERMISSION_LEVELS.NOT_ALLOWED,
    Moderation: PERMISSION_LEVELS.NOT_ALLOWED,
    Compliance: PERMISSION_LEVELS.NOT_ALLOWED,
    'Fraud & Security': PERMISSION_LEVELS.NOT_ALLOWED,
    Support: PERMISSION_LEVELS.LIMITED,
    Finance: PERMISSION_LEVELS.NOT_ALLOWED,
    'Reports Hub': PERMISSION_LEVELS.VIEW,
    'Website Content': PERMISSION_LEVELS.NOT_ALLOWED,
    'Team Members': PERMISSION_LEVELS.NOT_ALLOWED,
    Settings: PERMISSION_LEVELS.LIMITED,
  },
  moderator: {
    Dashboard: PERMISSION_LEVELS.VIEW,
    Customers: PERMISSION_LEVELS.VIEW,
    Businesses: PERMISSION_LEVELS.VIEW,
    Posts: PERMISSION_LEVELS.NOT_ALLOWED,
    Reviews: PERMISSION_LEVELS.NOT_ALLOWED,
    'Messages & Quotes': PERMISSION_LEVELS.NOT_ALLOWED,
    Moderation: PERMISSION_LEVELS.FULL,
    Compliance: PERMISSION_LEVELS.NOT_ALLOWED,
    'Fraud & Security': PERMISSION_LEVELS.NOT_ALLOWED,
    Support: PERMISSION_LEVELS.NOT_ALLOWED,
    Finance: PERMISSION_LEVELS.NOT_ALLOWED,
    'Reports Hub': PERMISSION_LEVELS.NOT_ALLOWED,
    'Website Content': PERMISSION_LEVELS.NOT_ALLOWED,
    'Team Members': PERMISSION_LEVELS.NOT_ALLOWED,
    Settings: PERMISSION_LEVELS.NOT_ALLOWED,
  },
  complianceOfficer: {
    Dashboard: PERMISSION_LEVELS.VIEW,
    Customers: PERMISSION_LEVELS.NOT_ALLOWED,
    Businesses: PERMISSION_LEVELS.VIEW,
    Posts: PERMISSION_LEVELS.NOT_ALLOWED,
    Reviews: PERMISSION_LEVELS.NOT_ALLOWED,
    'Messages & Quotes': PERMISSION_LEVELS.NOT_ALLOWED,
    Moderation: PERMISSION_LEVELS.NOT_ALLOWED,
    Compliance: PERMISSION_LEVELS.FULL,
    'Fraud & Security': PERMISSION_LEVELS.NOT_ALLOWED,
    Support: PERMISSION_LEVELS.NOT_ALLOWED,
    Finance: PERMISSION_LEVELS.NOT_ALLOWED,
    'Reports Hub': PERMISSION_LEVELS.NOT_ALLOWED,
    'Website Content': PERMISSION_LEVELS.NOT_ALLOWED,
    'Team Members': PERMISSION_LEVELS.NOT_ALLOWED,
    Settings: PERMISSION_LEVELS.NOT_ALLOWED,
  },
  fraudAnalyst: {
    Dashboard: PERMISSION_LEVELS.VIEW,
    Customers: PERMISSION_LEVELS.VIEW,
    Businesses: PERMISSION_LEVELS.VIEW,
    Posts: PERMISSION_LEVELS.NOT_ALLOWED,
    Reviews: PERMISSION_LEVELS.NOT_ALLOWED,
    'Messages & Quotes': PERMISSION_LEVELS.NOT_ALLOWED,
    Moderation: PERMISSION_LEVELS.NOT_ALLOWED,
    Compliance: PERMISSION_LEVELS.NOT_ALLOWED,
    'Fraud & Security': PERMISSION_LEVELS.FULL,
    Support: PERMISSION_LEVELS.NOT_ALLOWED,
    Finance: PERMISSION_LEVELS.NOT_ALLOWED,
    'Reports Hub': PERMISSION_LEVELS.NOT_ALLOWED,
    'Website Content': PERMISSION_LEVELS.NOT_ALLOWED,
    'Team Members': PERMISSION_LEVELS.NOT_ALLOWED,
    Settings: PERMISSION_LEVELS.NOT_ALLOWED,
  },
  supportAgent: {
    Dashboard: PERMISSION_LEVELS.VIEW,
    Customers: PERMISSION_LEVELS.VIEW,
    Businesses: PERMISSION_LEVELS.VIEW,
    Posts: PERMISSION_LEVELS.NOT_ALLOWED,
    Reviews: PERMISSION_LEVELS.NOT_ALLOWED,
    'Messages & Quotes': PERMISSION_LEVELS.NOT_ALLOWED,
    Moderation: PERMISSION_LEVELS.NOT_ALLOWED,
    Compliance: PERMISSION_LEVELS.NOT_ALLOWED,
    'Fraud & Security': PERMISSION_LEVELS.NOT_ALLOWED,
    Support: PERMISSION_LEVELS.FULL,
    Finance: PERMISSION_LEVELS.NOT_ALLOWED,
    'Reports Hub': PERMISSION_LEVELS.NOT_ALLOWED,
    'Website Content': PERMISSION_LEVELS.NOT_ALLOWED,
    'Team Members': PERMISSION_LEVELS.NOT_ALLOWED,
    Settings: PERMISSION_LEVELS.NOT_ALLOWED,
  },
  financeView: {
    Dashboard: PERMISSION_LEVELS.NOT_ALLOWED,
    Customers: PERMISSION_LEVELS.NOT_ALLOWED,
    Businesses: PERMISSION_LEVELS.NOT_ALLOWED,
    Posts: PERMISSION_LEVELS.NOT_ALLOWED,
    Reviews: PERMISSION_LEVELS.NOT_ALLOWED,
    'Messages & Quotes': PERMISSION_LEVELS.NOT_ALLOWED,
    Moderation: PERMISSION_LEVELS.NOT_ALLOWED,
    Compliance: PERMISSION_LEVELS.NOT_ALLOWED,
    'Fraud & Security': PERMISSION_LEVELS.NOT_ALLOWED,
    Support: PERMISSION_LEVELS.NOT_ALLOWED,
    Finance: PERMISSION_LEVELS.VIEW,
    'Reports Hub': PERMISSION_LEVELS.VIEW,
    'Website Content': PERMISSION_LEVELS.NOT_ALLOWED,
    'Team Members': PERMISSION_LEVELS.NOT_ALLOWED,
    Settings: PERMISSION_LEVELS.NOT_ALLOWED,
  },
  contentWriter: {
    Dashboard: PERMISSION_LEVELS.VIEW,
    Customers: PERMISSION_LEVELS.NOT_ALLOWED,
    Businesses: PERMISSION_LEVELS.NOT_ALLOWED,
    Posts: PERMISSION_LEVELS.NOT_ALLOWED,
    Reviews: PERMISSION_LEVELS.NOT_ALLOWED,
    'Messages & Quotes': PERMISSION_LEVELS.NOT_ALLOWED,
    Moderation: PERMISSION_LEVELS.NOT_ALLOWED,
    Compliance: PERMISSION_LEVELS.NOT_ALLOWED,
    'Fraud & Security': PERMISSION_LEVELS.NOT_ALLOWED,
    Support: PERMISSION_LEVELS.NOT_ALLOWED,
    Finance: PERMISSION_LEVELS.NOT_ALLOWED,
    'Reports Hub': PERMISSION_LEVELS.VIEW,
    'Website Content': PERMISSION_LEVELS.FULL,
    'Team Members': PERMISSION_LEVELS.NOT_ALLOWED,
    Settings: PERMISSION_LEVELS.NOT_ALLOWED,
  },
  readOnly: {
    Dashboard: PERMISSION_LEVELS.VIEW,
    Customers: PERMISSION_LEVELS.VIEW,
    Businesses: PERMISSION_LEVELS.VIEW,
    Posts: PERMISSION_LEVELS.VIEW,
    Reviews: PERMISSION_LEVELS.VIEW,
    'Messages & Quotes': PERMISSION_LEVELS.VIEW,
    Moderation: PERMISSION_LEVELS.VIEW,
    Compliance: PERMISSION_LEVELS.VIEW,
    'Fraud & Security': PERMISSION_LEVELS.VIEW,
    Support: PERMISSION_LEVELS.VIEW,
    Finance: PERMISSION_LEVELS.NOT_ALLOWED,
    'Reports Hub': PERMISSION_LEVELS.VIEW,
    'Website Content': PERMISSION_LEVELS.VIEW,
    'Team Members': PERMISSION_LEVELS.VIEW,
    Settings: PERMISSION_LEVELS.NOT_ALLOWED,
  },
};

// Modules granted for a role (any access except No Access)
const getModulesForRole = (roleId) => {
  const matrix = PERMISSION_MATRIX[roleId];
  if (!matrix) return [];
  return PERMISSION_MODULES.filter((mod) => matrix[mod] && matrix[mod] !== PERMISSION_LEVELS.NOT_ALLOWED);
};

const createEmptyModuleAccess = () =>
  Object.fromEntries(PERMISSION_MODULES.map((m) => [m, PERMISSION_LEVELS.NOT_ALLOWED]));

const moduleAccessFromMatrixKey = (matrixKey) => {
  const matrix = PERMISSION_MATRIX[matrixKey];
  const acc = createEmptyModuleAccess();
  if (!matrix) return acc;
  PERMISSION_MODULES.forEach((m) => {
    acc[m] = matrix[m] || PERMISSION_LEVELS.NOT_ALLOWED;
  });
  return acc;
};

const permissionsListFromModuleAccess = (moduleAccess) => {
  if (!moduleAccess) return [];
  return PERMISSION_MODULES.filter((m) => moduleAccess[m] && moduleAccess[m] !== PERMISSION_LEVELS.NOT_ALLOWED);
};

/** Dropdown: No Access, Read only (VIEW), Full — LIMITED from matrix maps to Read only in UI */
const MODULE_LEVEL_SELECT_OPTIONS = [
  { value: PERMISSION_LEVELS.NOT_ALLOWED, label: 'No Access' },
  { value: PERMISSION_LEVELS.VIEW, label: 'Read only' },
  { value: PERMISSION_LEVELS.FULL, label: 'Full' },
];

const normalizeModuleLevelForSelect = (level) => {
  if (level === PERMISSION_LEVELS.LIMITED) return PERMISSION_LEVELS.VIEW;
  if (
    level === PERMISSION_LEVELS.NOT_ALLOWED ||
    level === PERMISSION_LEVELS.VIEW ||
    level === PERMISSION_LEVELS.FULL
  ) {
    return level;
  }
  return PERMISSION_LEVELS.NOT_ALLOWED;
};

const DEFAULT_RESTRICTIONS = () => ({
  canCreateRoles: false,
  canEditRoles: false,
  canAssignCoOwner: false,
  canAssignOwner: false,
  canEditOwnerAuthority: false,
  canDeactivateMembers: false,
  canResetPasswords: false,
  canExportFinanceData: false,
});

const RESTRICTION_ROWS = [
  { key: 'canCreateRoles', label: 'Can create roles' },
  { key: 'canEditRoles', label: 'Can edit roles' },
  { key: 'canAssignCoOwner', label: 'Can assign Co-Owner' },
  { key: 'canAssignOwner', label: 'Can assign Owner', locked: true },
  { key: 'canEditOwnerAuthority', label: 'Can edit Owner authority' },
  { key: 'canDeactivateMembers', label: 'Can deactivate members' },
  { key: 'canResetPasswords', label: 'Can reset passwords' },
  { key: 'canExportFinanceData', label: 'Can export finance data' },
];

const RestrictionToggle = ({ checked, onChange, disabled }) => (
  <label
    className={`relative inline-flex items-center shrink-0 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <input
      type="checkbox"
      role="switch"
      aria-checked={checked}
      className="sr-only peer"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
    />
    <div
      className="relative w-9 h-5 bg-lightgray rounded-full peer-checked:bg-primaryblue after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4 peer-disabled:opacity-50"
      aria-hidden
    />
  </label>
);

const resolveModuleAccessForRole = (role) => {
  if (role.moduleAccess) {
    return { ...createEmptyModuleAccess(), ...role.moduleAccess };
  }
  if (role.id && PERMISSION_MATRIX[role.id]) {
    return moduleAccessFromMatrixKey(role.id);
  }
  const acc = createEmptyModuleAccess();
  const perms = role.permissions || [];
  if (perms.includes(ALL_MODULES_LABEL)) {
    PERMISSION_MODULES.forEach((m) => {
      acc[m] = PERMISSION_LEVELS.FULL;
    });
    return acc;
  }
  perms.forEach((m) => {
    if (PERMISSION_MODULES.includes(m)) acc[m] = PERMISSION_LEVELS.FULL;
  });
  return acc;
};

const buildNewRoleFormState = () => ({
  name: '',
  moduleAccess: createEmptyModuleAccess(),
});

const ROLE_NAME_TO_ID = {
  'Co-Owner': 'coOwner',
  'Operations Admin': 'operationsAdmin',
  'Technical Support': 'technicalSupport',
  Moderator: 'moderator',
  'Compliance Officer': 'complianceOfficer',
  'Fraud Analyst': 'fraudAnalyst',
  'Support Agent': 'supportAgent',
  'Finance View': 'financeView',
  'Content Writer': 'contentWriter',
  'Read-Only': 'readOnly',
  'Technical Limited Access': 'technicalSupport',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
  return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const todayFormatted = () =>
  new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

const MOCK_TEAM_MEMBERS = [
  { id: 'tm-1', name: 'Saim Sahi', status: 'Active', isOwner: true },
  { id: 'tm-2', name: 'Hamza Zahoor', status: 'Active', isOwner: false },
  { id: 'tm-3', name: 'Daniel Carter', status: 'Active', isOwner: false },
  { id: 'tm-4', name: 'Emma Roberts', status: 'Active', isOwner: false },
  { id: 'tm-5', name: 'Michael Tan', status: 'Active', isOwner: false },
  { id: 'tm-6', name: 'Olivia Smith', status: 'Active', isOwner: false },
  { id: 'tm-7', name: 'Jordan Lee', status: 'Active', isOwner: false },
  { id: 'tm-8', name: 'Alex Johnson', status: 'Active', isOwner: false },
  { id: 'tm-9', name: 'Mia Collins', status: 'Pending Role', isOwner: false },
];

const buildInitialAssignments = () => [
  {
    id: 'ra-2',
    teamMemberId: 'tm-2',
    teamMemberName: 'Hamza Zahoor',
    roleName: 'Co-Owner',
    permissions: getModulesForRole('coOwner'),
    status: 'Active',
    assignedOn: '2026-02-10',
    lastUpdated: '2026-02-10',
    isOwner: false,
  },
  {
    id: 'ra-3',
    teamMemberId: 'tm-3',
    teamMemberName: 'Daniel Carter',
    roleName: 'Technical Support',
    permissions: getModulesForRole('technicalSupport'),
    status: 'Active',
    assignedOn: '2026-02-12',
    lastUpdated: '2026-02-12',
    isOwner: false,
  },
  {
    id: 'ra-4',
    teamMemberId: 'tm-4',
    teamMemberName: 'Emma Roberts',
    roleName: 'Content Writer',
    permissions: getModulesForRole('contentWriter'),
    status: 'Active',
    assignedOn: '2026-02-08',
    lastUpdated: '2026-02-08',
    isOwner: false,
  },
  {
    id: 'ra-5',
    teamMemberId: 'tm-5',
    teamMemberName: 'Michael Tan',
    roleName: 'Finance View',
    permissions: getModulesForRole('financeView'),
    status: 'Active',
    assignedOn: '2026-02-08',
    lastUpdated: '2026-02-08',
    isOwner: false,
  },
  {
    id: 'ra-6',
    teamMemberId: 'tm-6',
    teamMemberName: 'Olivia Smith',
    roleName: 'Compliance Officer',
    permissions: getModulesForRole('complianceOfficer'),
    status: 'Active',
    assignedOn: '2026-02-10',
    lastUpdated: '2026-02-10',
    isOwner: false,
  },
  {
    id: 'ra-7',
    teamMemberId: 'tm-7',
    teamMemberName: 'Jordan Lee',
    roleName: 'Support Agent',
    permissions: getModulesForRole('supportAgent'),
    status: 'Active',
    assignedOn: '2026-02-16',
    lastUpdated: '2026-02-16',
    isOwner: false,
  },
  {
    id: 'ra-8',
    teamMemberId: 'tm-8',
    teamMemberName: 'Alex Johnson',
    roleName: 'Technical Limited Access',
    permissions: getModulesForRole('technicalSupport'),
    status: 'Active',
    assignedOn: '2026-02-13',
    lastUpdated: '2026-02-13',
    isOwner: false,
  },
];

const buildInitialRoles = () => {
  const mk = (id, name, primaryUse, accessScope, members, lastUpdated, matrixKey) => ({
    id,
    name,
    roleType: 'Standard Role',
    primaryUse,
    accessScope,
    description: primaryUse,
    moduleAccess: moduleAccessFromMatrixKey(matrixKey),
    restrictions: { ...DEFAULT_RESTRICTIONS(), canAssignOwner: false },
    permissions: getModulesForRole(matrixKey),
    members,
    status: 'Active',
    createdBy: 'Saim Sahi',
    createdAt: '02 Jan 2025',
    lastUpdated,
    isCore: false,
  });
  return [
    mk('coOwner', 'Co-Owner', 'Broad platform authority below Owner', 'Broad cross-module access', 1, '15 Feb 2026', 'coOwner'),
    mk('operationsAdmin', 'Operations Admin', 'Day-to-day platform operations', 'Broad operational access', 0, '12 Feb 2026', 'operationsAdmin'),
    mk('technicalSupport', 'Technical Support', 'Technical setup and maintenance', 'Selected technical areas', 1, '12 Feb 2026', 'technicalSupport'),
    mk('moderator', 'Moderator', 'Moderation queue and archive handling', 'Moderation-only access', 0, '12 Feb 2026', 'moderator'),
    mk('complianceOfficer', 'Compliance Officer', 'Compliance queue and archive handling', 'Compliance-only access', 1, '10 Feb 2026', 'complianceOfficer'),
    mk('fraudAnalyst', 'Fraud Analyst', 'Fraud & Security queue and archive handling', 'Fraud-only access', 0, '10 Feb 2026', 'fraudAnalyst'),
    mk('supportAgent', 'Support Agent', 'Support ticket handling', 'Support-only access', 1, '09 Feb 2026', 'supportAgent'),
    mk('financeView', 'Finance View', 'Finance visibility and review', 'Finance read-only access', 1, '08 Feb 2026', 'financeView'),
    mk('contentWriter', 'Content Writer', 'Website and public content management', 'Content-only access', 1, '08 Feb 2026', 'contentWriter'),
    mk('readOnly', 'Read-Only', 'Observation-only access', 'View-only across approved areas', 0, '08 Feb 2026', 'readOnly'),
  ];
};

const RolesPermissions = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { roleId: editRoleId, assignmentId: editAssignmentId } = useParams();
  const isCreateRolePage = location.pathname === '/admin/settings/rbac/roles/new';
  const isEditRolePage = location.pathname.includes('/roles/') && location.pathname.endsWith('/edit') && editRoleId;
  const isEditAssignmentPage = location.pathname.includes('/assignment/') && location.pathname.endsWith('/edit') && editAssignmentId;
  const isRolesLibraryPage = location.pathname === '/admin/settings/rbac';
  const isAssignmentsListPage = location.pathname === '/admin/settings/rbac/assignments';
  const isAssignRolePage = location.pathname === '/admin/settings/rbac/assign';

  const rbacDetailPathMatch = location.pathname.match(/^\/admin\/settings\/rbac\/([^/]+)$/);
  const rbacDetailReservedSegments = new Set(['assign', 'assignments']);
  const viewRoleId =
    rbacDetailPathMatch && !rbacDetailReservedSegments.has(rbacDetailPathMatch[1])
      ? rbacDetailPathMatch[1]
      : null;
  const isRoleDetailViewPage = Boolean(viewRoleId);

  const [roles, setRoles] = useState(buildInitialRoles());
  const [assignments, setAssignments] = useState(buildInitialAssignments);
  const [newRole, setNewRole] = useState(buildNewRoleFormState);
  const [editingRole, setEditingRole] = useState(null); // full role form incl. moduleAccess, restrictions
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(null); // assignment id
  const [removeModalOpen, setRemoveModalOpen] = useState(null);

  // Assign Role page (independent view)
  const [assignMemberId, setAssignMemberId] = useState('');
  const [assignSelectedRoleId, setAssignSelectedRoleId] = useState('');

  // Edit flow (assignment)
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [editSelectedRoleId, setEditSelectedRoleId] = useState('');
  const [editAccessModules, setEditAccessModules] = useState([]);

  const [rowActionsOpen, setRowActionsOpen] = useState({});
  const [rowMenuPosition, setRowMenuPosition] = useState(null);
  const [rowMenuPlacementReady, setRowMenuPlacementReady] = useState(false);
  const rowActionsRefs = useRef({});

    const viewPageRole = useMemo(
    () => (viewRoleId ? roles.find((r) => r.id === viewRoleId) ?? null : null),
    [viewRoleId, roles]
  );

  const handleAssignSave = () => {
    const member = MOCK_TEAM_MEMBERS.find((m) => m.id === assignMemberId);
    const role = roles.find((r) => r.id === assignSelectedRoleId);
    if (!member || !role) return;
    const permissions = role.permissions?.includes(ALL_MODULES_LABEL)
      ? [ALL_MODULES_LABEL]
      : [...(role.permissions || [])];
    const now = todayFormatted();
    setAssignments((prev) => [
      ...prev,
      {
        id: `ra-${Date.now()}`,
        teamMemberId: member.id,
        teamMemberName: member.name,
        roleName: role.name,
        permissions,
        status: 'Active',
        assignedOn: now,
        lastUpdated: now,
        isOwner: false,
      },
    ]);
    setAssignMemberId('');
    setAssignSelectedRoleId('');
    navigate('/admin/settings/team-members');
  };

  const handleAssignCancel = () => {
    setAssignMemberId('');
    setAssignSelectedRoleId('');
    navigate('/admin/settings/rbac');
  };

  useEffect(() => {
    if (isEditAssignmentPage && editAssignmentId) {
      const assignment = assignments.find((a) => a.id === editAssignmentId);
      if (assignment && !assignment.isOwner) {
        setEditingAssignment(assignment);
        const matchedRole = roles.find((r) => r.name === (assignment.roleName || '').trim());
        setEditSelectedRoleId(matchedRole ? matchedRole.id : '');
        const perms = assignment.permissions || [];
        if (perms.includes(ALL_MODULES_LABEL)) {
          setEditAccessModules([...PERMISSION_MODULES]);
        } else {
          setEditAccessModules(PERMISSION_MODULES.filter((m) => perms.includes(m)));
        }
      } else {
        setEditingAssignment(null);
        setEditSelectedRoleId('');
        setEditAccessModules([]);
      }
    } else {
      setEditingAssignment(null);
      setEditSelectedRoleId('');
      setEditAccessModules([]);
    }
  }, [isEditAssignmentPage, editAssignmentId, assignments, roles]);

  useEffect(() => {
    if (isAssignRolePage && location.state?.preselectMemberId) {
      setAssignMemberId(location.state.preselectMemberId);
    }
  }, [isAssignRolePage, location.state?.preselectMemberId]);

  const handleEditAssignmentSave = () => {
    if (!editingAssignment) return;
    const role = roles.find((r) => r.id === editSelectedRoleId);
    if (!role) return;
    if (!editAccessModules.length) return;
    const permissions =
      editAccessModules.length >= PERMISSION_MODULES.length
        ? [ALL_MODULES_LABEL]
        : [...editAccessModules];
    setAssignments((prev) =>
      prev.map((a) =>
        a.id === editingAssignment.id
          ? {
              ...a,
              roleName: role.name,
              permissions,
              lastUpdated: todayFormatted(),
            }
          : a
      )
    );
    setEditingAssignment(null);
    setEditSelectedRoleId('');
    setEditAccessModules([]);
    navigate('/admin/settings/team-members');
  };

  const handleEditAssignmentCancel = () => {
    setEditingAssignment(null);
    setEditSelectedRoleId('');
    setEditAccessModules([]);
    navigate('/admin/settings/team-members');
  };

  const toggleEditAccessModule = (mod) => {
    setEditAccessModules((prev) => {
      const next = new Set(prev);
      if (next.has(mod)) next.delete(mod);
      else next.add(mod);
      return [...next];
    });
  };

  const handleDeactivateConfirm = () => {
    if (!deactivateModalOpen) return;
    setAssignments((prev) =>
      prev.map((a) => (a.id === deactivateModalOpen ? { ...a, status: 'Inactive' } : a))
    );
    setDeactivateModalOpen(null);
    setRowActionsOpen((prev) => ({ ...prev, [deactivateModalOpen]: false }));
  };

  const handleReactivate = (assignment) => {
    setAssignments((prev) =>
      prev.map((a) => (a.id === assignment.id ? { ...a, status: 'Active' } : a))
    );
    setRowActionsOpen((prev) => ({ ...prev, [assignment.id]: false }));
  };

  const handleRemoveConfirm = () => {
    if (!removeModalOpen) return;
    setAssignments((prev) => prev.filter((a) => a.id !== removeModalOpen));
    setRemoveModalOpen(null);
    setRowActionsOpen((prev) => ({ ...prev, [removeModalOpen]: false }));
  };

  const toggleRowActions = (id) => {
    setRowActionsOpen((prev) => {
      if (prev[id]) return {};
      return { [id]: true };
    });
  };

  const deriveRoleAccessScopeFromModuleAccess = (moduleAccess) => {
    const list = permissionsListFromModuleAccess(moduleAccess);
    if (!list.length) return 'No modules assigned';
    return `${list.length} module${list.length === 1 ? '' : 's'}`;
  };

  const handleCreateRoleSave = () => {
    if (!newRole.name.trim()) return;
    const perms = permissionsListFromModuleAccess(newRole.moduleAccess);
    const id = newRole.name.toLowerCase().replace(/\s+/g, '-');
    const now = todayFormatted();
    const nameTrim = newRole.name.trim();
    const restrictions = { ...DEFAULT_RESTRICTIONS(), canAssignOwner: false };
    setRoles((prev) => [
      ...prev,
      {
        id,
        name: nameTrim,
        roleType: 'Standard Role',
        primaryUse: nameTrim,
        accessScope: deriveRoleAccessScopeFromModuleAccess(newRole.moduleAccess),
        description: '',
        moduleAccess: { ...newRole.moduleAccess },
        restrictions,
        permissions: perms,
        members: 0,
        status: 'Active',
        createdBy: 'Admin (SA-001)',
        createdAt: now,
        lastUpdated: now,
        isCore: false,
      },
    ]);
    setNewRole(buildNewRoleFormState());
    navigate('/admin/settings/rbac');
  };

  const handleCreateRoleCancel = () => {
    setNewRole(buildNewRoleFormState());
    navigate('/admin/settings/rbac');
  };

  useEffect(() => {
    if (isEditRolePage && editRoleId) {
      const role = roles.find((r) => r.id === editRoleId);
      if (role) {
        setEditingRole({
          id: role.id,
          name: role.name,
          description: role.description,
          status: role.status,
          roleType: role.roleType || 'Standard Role',
          moduleAccess: resolveModuleAccessForRole(role),
          restrictions: { ...DEFAULT_RESTRICTIONS(), ...role.restrictions, canAssignOwner: false },
          isCore: role.isCore,
        });
      } else {
        setEditingRole(null);
      }
    } else {
      setEditingRole(null);
    }
  }, [isEditRolePage, editRoleId, roles]);

  const handleEditRoleSave = () => {
    if (!editingRole || !editingRole.name?.trim() || !editingRole.description?.trim()) return;
    const perms = permissionsListFromModuleAccess(editingRole.moduleAccess);
    const now = todayFormatted();
    const restrictions = { ...editingRole.restrictions, canAssignOwner: false };
    setRoles((prev) =>
      prev.map((r) =>
        r.id === editingRole.id
          ? {
              ...r,
              name: editingRole.name.trim(),
              description: editingRole.description.trim(),
              status: editingRole.status,
              moduleAccess: { ...editingRole.moduleAccess },
              restrictions,
              permissions: perms,
              lastUpdated: now,
              ...(!editingRole.isCore
                ? {
                    primaryUse: editingRole.description.trim(),
                    accessScope: deriveRoleAccessScopeFromModuleAccess(editingRole.moduleAccess),
                  }
                : {}),
            }
          : r
      )
    );
    setEditingRole(null);
    navigate('/admin/settings/rbac');
  };

  const handleEditRoleCancel = () => {
    setEditingRole(null);
    navigate('/admin/settings/rbac');
  };

  const rowMenuDropdownRef = useRef(null);
  const openRowId = Object.keys(rowActionsOpen).find((id) => rowActionsOpen[id]) ?? null;

  useLayoutEffect(() => {
    if (!openRowId) {
      setRowMenuPosition(null);
      setRowMenuPlacementReady(false);
      return;
    }
    const gap = 4;
    const viewportPad = 8;

    setRowMenuPlacementReady(false);

    const trigger = rowActionsRefs.current[openRowId];
    if (!trigger) return;
    const menuW = openRowId.startsWith('lib-') ? 140 : 200;
    const r0 = trigger.getBoundingClientRect();
    setRowMenuPosition({
      top: r0.bottom + gap,
      left: Math.max(viewportPad, r0.right - menuW),
    });

    let cancelled = false;

    const finalize = () => {
      if (cancelled) return;
      const tr = rowActionsRefs.current[openRowId];
      if (!tr) {
        setRowMenuPlacementReady(true);
        return;
      }
      const r = tr.getBoundingClientRect();
      const mw = openRowId.startsWith('lib-') ? 140 : 200;
      const left = Math.max(viewportPad, r.right - mw);
      const menu = rowMenuDropdownRef.current;
      const h = menu?.offsetHeight ?? 0;
      let top = r.bottom + gap;
      if (h > 0 && top + h > window.innerHeight - viewportPad) {
        top = Math.max(viewportPad, r.top - h - gap);
      }
      setRowMenuPosition({ top, left });
      setRowMenuPlacementReady(true);
    };

    const rafId = requestAnimationFrame(() => {
      if (cancelled) return;
      requestAnimationFrame(finalize);
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [openRowId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const rowMenuEl = rowMenuDropdownRef.current;
      Object.keys(rowActionsRefs.current).forEach((id) => {
        const ref = rowActionsRefs.current[id];
        const insideButton = ref && ref.contains(e.target);
        const insideMenu = rowMenuEl && rowMenuEl.contains(e.target);
        if (!insideButton && !insideMenu) setRowActionsOpen((prev) => ({ ...prev, [id]: false }));
      });
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const permissionDisplay = (assignment) => {
    if (assignment.permissions.includes(ALL_MODULES_LABEL)) return ALL_MODULES_LABEL;
    const roleId = ROLE_NAME_TO_ID[assignment.roleName];
    const matrix = roleId && PERMISSION_MATRIX[roleId];
    if (matrix) {
      const parts = assignment.permissions.map((mod) => {
        const level = matrix[mod];
        if (level === PERMISSION_LEVELS.LIMITED) return `${mod} (Limited)`;
        if (level === PERMISSION_LEVELS.VIEW) return `${mod} (View Only)`;
        return mod;
      });
      return parts.join(', ');
    }
    return assignment.permissions.join(', ');
  };

  /* Actions column minimal; freed fr split across the other columns */
  const gridCols =
    'minmax(140px, 1.58fr) minmax(160px, 2.08fr) minmax(80px, 0.98fr) minmax(100px, 1.18fr) minmax(100px, 1.18fr) minmax(32px, 0.22fr)';

  const roleLibraryGridCols =
    'minmax(120px,1.35fr) minmax(56px,0.65fr) minmax(120px,1.15fr) minmax(72px,0.72fr) minmax(100px,0.95fr) minmax(32px,0.22fr)';

  return (
    <AdminLayout>
      <div>
        <div>
        {isCreateRolePage ? (
          <>
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <AdminPanelTitle className="text-offblack">Create New Role</AdminPanelTitle>
            <div className="space-y-4">
              <div className="space-y-1">
                <AdminDetailFieldLabel>Role Name *</AdminDetailFieldLabel>
                <input
                  type="text"
                  value={newRole.name}
                  onChange={(e) => setNewRole((p) => ({ ...p, name: e.target.value }))}
                  className={adminFormDetailInputClasses()}
                  placeholder="Enter role name"
                />
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Modules (permissions)</AdminDetailFieldLabel>
                <div className="space-y-1 max-w-3xl">
                  {PERMISSION_MODULES.map((mod) => (
                    <div
                      key={mod}
                      className="grid grid-cols-1 sm:grid-cols-[11rem_minmax(12rem,280px)] gap-x-4 gap-y-1 items-center min-w-0 text-left"
                    >
                      <span className="text-offblack min-w-0">{mod}</span>
                      <select
                        value={normalizeModuleLevelForSelect(newRole.moduleAccess[mod])}
                        onChange={(e) =>
                          setNewRole((p) => ({
                            ...p,
                            moduleAccess: { ...p.moduleAccess, [mod]: e.target.value },
                          }))
                        }
                        className={`${adminFormDetailSelectClasses()} w-full justify-self-stretch text-left text-sm`}
                      >
                        {MODULE_LEVEL_SELECT_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </DashboardMainContainer>
          <div className={ADMIN_FORM_FOOTER_CLASS}>
            <div className="flex flex-wrap items-center justify-end gap-3">
              <AdminFormSecondaryAction type="button" onClick={handleCreateRoleCancel}>
                Cancel
              </AdminFormSecondaryAction>
              <AdminHeaderAction onClick={handleCreateRoleSave}
                disabled={!newRole.name.trim()}>Create Role</AdminHeaderAction>
            </div>
          </div>
          </>
        ) : isEditRolePage && editingRole ? (
          <>
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <AdminPanelTitle className="text-offblack">Edit Role</AdminPanelTitle>
            <div className="space-y-4">
              <div className="space-y-1">
                <AdminDetailFieldLabel>Role Name *</AdminDetailFieldLabel>
                <input
                  type="text"
                  value={editingRole.name}
                  onChange={(e) => setEditingRole((p) => ({ ...p, name: e.target.value }))}
                  disabled={editingRole.isCore}
                  className={adminFormDetailInputClasses(editingRole.isCore)}
                  placeholder="Enter role name"
                />
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Role Type</AdminDetailFieldLabel>
                <select
                  value={editingRole.roleType}
                  disabled
                  className={adminFormDetailSelectClasses(true)}
                >
                  <option value="Standard Role">Standard Role</option>
                </select>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Status</AdminDetailFieldLabel>
                <select
                  value={editingRole.status}
                  onChange={(e) => setEditingRole((p) => ({ ...p, status: e.target.value }))}
                  disabled={editingRole.isCore}
                  className={adminFormDetailSelectClasses(editingRole.isCore)}
                >
                  <option value="Active">Active</option>
                  <option value="Disabled">Disabled</option>
                </select>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Description *</AdminDetailFieldLabel>
                <textarea
                  value={editingRole.description}
                  onChange={(e) => setEditingRole((p) => ({ ...p, description: e.target.value }))}
                  disabled={editingRole.isCore}
                  className={adminFormDetailTextareaClasses(editingRole.isCore)}
                  placeholder="Enter role description"
                />
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Modules (permissions)</AdminDetailFieldLabel>
                <div className="space-y-1 max-w-3xl">
                  {PERMISSION_MODULES.map((mod) => (
                    <div
                      key={mod}
                      className="grid grid-cols-1 sm:grid-cols-[11rem_minmax(12rem,280px)] gap-x-4 gap-y-1 items-center min-w-0 text-left"
                    >
                      <span className="text-offblack min-w-0">{mod}</span>
                      <select
                        value={normalizeModuleLevelForSelect(editingRole.moduleAccess[mod])}
                        onChange={(e) =>
                          setEditingRole((p) => ({
                            ...p,
                            moduleAccess: { ...p.moduleAccess, [mod]: e.target.value },
                          }))
                        }
                        disabled={editingRole.isCore}
                        className={`${adminFormDetailSelectClasses(editingRole.isCore)} w-full justify-self-stretch text-left text-sm`}
                      >
                        {MODULE_LEVEL_SELECT_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Restrictions</AdminDetailFieldLabel>
                <div className="space-y-1 max-w-3xl">
                  {RESTRICTION_ROWS.map(({ key, label, locked }) => (
                    <div
                      key={key}
                      className="grid grid-cols-1 sm:grid-cols-[11rem_auto] gap-x-1 gap-y-1 items-start sm:items-center min-w-0 text-left"
                    >
                      <span className="text-offblack text-sm min-w-0">{label}</span>
                      <div className="justify-self-start">
                        {locked ? (
                          <RestrictionToggle checked={false} onChange={() => {}} disabled />
                        ) : (
                          <RestrictionToggle
                            checked={Boolean(editingRole.restrictions[key])}
                            onChange={(on) =>
                              setEditingRole((p) => ({
                                ...p,
                                restrictions: { ...p.restrictions, [key]: on },
                              }))
                            }
                            disabled={editingRole.isCore}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <AdminSupportingText className="text-mediumgray mt-2 block">
                  Can assign Owner stays No (system-locked). Owner is only changed via Ownership Transfer.
                </AdminSupportingText>
              </div>
            </div>
          </DashboardMainContainer>
          <div className={ADMIN_FORM_FOOTER_CLASS}>
            <div className="flex flex-wrap items-center justify-end gap-3">
              <AdminFormSecondaryAction type="button" onClick={handleEditRoleCancel}>
                Cancel
              </AdminFormSecondaryAction>
              <AdminHeaderAction onClick={handleEditRoleSave}
                disabled={editingRole.isCore || !editingRole.name.trim() || !editingRole.description.trim()}>Save</AdminHeaderAction>
            </div>
          </div>
          </>
        ) : isEditRolePage ? (
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <AdminSupportingText>Role not found.</AdminSupportingText>
            <AdminLinkAction type="button" onClick={() => navigate('/admin/settings/rbac')} className="mt-2">
              Back to Roles &amp; Access
            </AdminLinkAction>
          </DashboardMainContainer>
        ) : isRoleDetailViewPage && viewPageRole ? (
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <AdminPanelTitle className="text-offblack">{viewPageRole.name}</AdminPanelTitle>
              <AdminHeaderAction
                type="button"
                className="self-start sm:self-auto"
                onClick={() => navigate(`/admin/settings/rbac/roles/${viewPageRole.id}/edit`)}
              >
                Edit role
              </AdminHeaderAction>
            </div>
            <div className="space-y-4">
              <div className="space-y-1">
                <AdminDetailFieldLabel>Role Type</AdminDetailFieldLabel>
                <span className="text-offblack">{viewPageRole.roleType || 'Standard Role'}</span>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Status</AdminDetailFieldLabel>
                <span className="text-offblack">{viewPageRole.status}</span>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Description</AdminDetailFieldLabel>
                <span className="text-offblack whitespace-pre-wrap">{viewPageRole.description || '—'}</span>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Modules (permissions)</AdminDetailFieldLabel>
                <div className="space-y-1 max-w-3xl">
                  {(() => {
                    const viewMa = resolveModuleAccessForRole(viewPageRole);
                    return PERMISSION_MODULES.map((mod) => {
                      const level = viewMa[mod] || PERMISSION_LEVELS.NOT_ALLOWED;
                      return (
                        <div
                          key={mod}
                          className="grid grid-cols-1 sm:grid-cols-[11rem_minmax(12rem,280px)] gap-x-4 gap-y-1 items-center min-w-0 text-left"
                        >
                          <span className="text-offblack min-w-0">{mod}</span>
                          <span className="text-offblack text-sm sm:w-[200px] justify-self-start">{level}</span>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
              <div className="space-y-1">
                <AdminDetailFieldLabel>Restrictions</AdminDetailFieldLabel>
                <div className="space-y-1 max-w-3xl">
                  {(() => {
                    const viewRestr = { ...DEFAULT_RESTRICTIONS(), ...viewPageRole.restrictions, canAssignOwner: false };
                    return RESTRICTION_ROWS.map(({ key, label, locked }) => {
                      const on = Boolean(viewRestr[key]);
                      return (
                        <div
                          key={key}
                          className="grid grid-cols-1 sm:grid-cols-[11rem_auto] gap-x-1 gap-y-1 items-start sm:items-center min-w-0 text-left"
                        >
                          <span className="text-offblack text-sm min-w-0">{label}</span>
                          <span className="text-offblack text-sm justify-self-start">{locked ? 'No' : on ? 'Yes' : 'No'}</span>
                        </div>
                      );
                    });
                  })()}
                </div>
                <AdminSupportingText className="text-mediumgray mt-2 block">
                  Can assign Owner stays No (system-locked). Owner is only changed via Ownership Transfer.
                </AdminSupportingText>
              </div>
            </div>
          </DashboardMainContainer>
        ) : isRoleDetailViewPage ? (
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <AdminSupportingText>Role not found.</AdminSupportingText>
            <AdminLinkAction type="button" onClick={() => navigate('/admin/settings/rbac')} className="mt-2">
              Back to Roles &amp; Access
            </AdminLinkAction>
          </DashboardMainContainer>
        ) : isRolesLibraryPage ? (
          <>
            <AdminPageTitle>Roles &amp; Access</AdminPageTitle>
            <div className={ADMIN_TOOLBAR_CLASS}>
              <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end">
                <AdminHeaderAction
                  type="button"
                  onClick={() => navigate('/admin/settings/rbac/roles/new')}
                >
                  Create Role
                </AdminHeaderAction>
              </div>
            </div>

            <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
              <div className="overflow-x-auto overflow-y-visible">
              <div
                className="grid py-3 text-offblack border-b border-lightgray px-4 gap-3 min-w-[720px]"
                style={{ gridTemplateColumns: roleLibraryGridCols }}
              >
                <AdminTableHeader>Role Name</AdminTableHeader>
                <AdminTableHeader>Members</AdminTableHeader>
                <AdminTableHeader>Access Scope</AdminTableHeader>
                <AdminTableHeader>Status</AdminTableHeader>
                <AdminTableHeader>Last Updated</AdminTableHeader>
                <AdminTableHeader>Actions</AdminTableHeader>
              </div>
              {roles.map((role) => (
                <div
                  key={role.id}
                  className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-3 items-center min-w-[720px]"
                  style={{ gridTemplateColumns: roleLibraryGridCols }}
                >
                  <span className="font-medium truncate" title={role.name}>
                    {role.name}
                  </span>
                  <span className="text-sm">{role.members}</span>
                  <span className="text-sm text-offblack line-clamp-2" title={role.accessScope}>
                    {role.accessScope}
                  </span>
                  <div>
                    <AdminStatusBadge status={role.status} label={role.status} />
                  </div>
                  <span className="text-sm whitespace-nowrap">{role.lastUpdated}</span>
                  <div className="flex items-center min-w-0 shrink-0 relative" onClick={(e) => e.stopPropagation()}>
                    <div
                      className="relative inline-flex"
                      ref={(el) => {
                        rowActionsRefs.current[`lib-${role.id}`] = el;
                      }}
                    >
                      <AdminRowActionsButton
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRowActions(`lib-${role.id}`);
                        }}
                        title="Actions"
                        aria-label="Row actions"
                      >
                        <AdminRowMenuIcon />
                      </AdminRowActionsButton>
                    </div>
                  </div>
                </div>
              ))}
              </div>
            </DashboardMainContainer>
            {openRowId && rowMenuPosition && openRowId.startsWith('lib-') && (() => {
              const rid = openRowId.slice(4);
              const role = roles.find((r) => r.id === rid);
              if (!role) return null;
              return createPortal(
                <div
                  ref={rowMenuDropdownRef}
                  className={`fixed bg-white rounded-lg min-w-[200px] w-max max-w-[16rem] shadow-lg border border-lightgray z-[9999] py-2 ${
                    rowMenuPlacementReady ? 'visible' : 'invisible pointer-events-none'
                  }`}
                  style={{ top: rowMenuPosition.top, left: rowMenuPosition.left }}
                >
                  <AdminRowMenuItem
                    onClick={() => {
                      navigate(`/admin/settings/rbac/${role.id}`);
                      setRowActionsOpen((prev) => ({ ...prev, [`lib-${role.id}`]: false }));
                    }}
                  >
                    View
                  </AdminRowMenuItem>
                  <AdminRowMenuItem
                    onClick={() => {
                      navigate(`/admin/settings/rbac/roles/${role.id}/edit`);
                      setRowActionsOpen((prev) => ({ ...prev, [`lib-${role.id}`]: false }));
                    }}
                  >
                    Edit
                  </AdminRowMenuItem>
                </div>,
                document.body
              );
            })()}
          </>
        ) : isEditAssignmentPage && editingAssignment ? (
          <>
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <AdminPanelTitle className="text-offblack">Change Role</AdminPanelTitle>
            <div className="space-y-6">
              <div className="space-y-2">
                <AdminDetailFieldLabel>Existing roles</AdminDetailFieldLabel>
                <div className="space-y-2">
                  {roles.map((r) => (
                    <label
                      key={r.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        editSelectedRoleId === r.id ? 'border-primaryblue bg-primaryblue/5' : 'border-lightgray hover:border-primaryblue/50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="editAssignRole"
                        value={r.id}
                        checked={editSelectedRoleId === r.id}
                        onChange={() => {
                          setEditSelectedRoleId(r.id);
                          const role = roles.find((x) => x.id === r.id);
                          if (role) {
                            const next = role.permissions?.includes(ALL_MODULES_LABEL)
                              ? [...PERMISSION_MODULES]
                              : [...(role.permissions || [])].filter((p) => PERMISSION_MODULES.includes(p));
                            setEditAccessModules(next);
                          }
                        }}
                        className="mt-1 rounded-full border-lightgray text-primaryblue focus:ring-primaryblue"
                      />
                      <div className="min-w-0 flex-1">
                        <AdminTableHeader>{r.name}</AdminTableHeader>
                        <span className="text-mediumgray text-xs block mt-0.5">{r.description}</span>
                        <span className="text-offblack text-xs block mt-1">
                          {r.permissions?.includes(ALL_MODULES_LABEL) ? ALL_MODULES_LABEL : (r.permissions || []).join(', ')}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <AdminDetailFieldLabel>Permission</AdminDetailFieldLabel>
                <AdminSupportingText className="text-mediumgray mb-2 block text-sm">
                  Defaults follow the selected role; adjust permissions as needed.
                </AdminSupportingText>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 w-full min-w-0 gap-2">
                  {PERMISSION_MODULES.map((mod) => {
                    const checked = editAccessModules.includes(mod);
                    return (
                      <label
                        key={mod}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          checked ? 'border-primaryblue bg-primaryblue/5' : 'border-lightgray hover:border-primaryblue/50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleEditAccessModule(mod)}
                          className="h-4 w-4 shrink-0 rounded border-lightgray text-primaryblue focus:ring-primaryblue"
                        />
                        <div className="min-w-0 flex-1">
                          <AdminTableHeader>{mod}</AdminTableHeader>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          </DashboardMainContainer>
          <div className={ADMIN_FORM_FOOTER_CLASS}>
            <div className="flex flex-wrap items-center justify-end gap-3">
              <AdminFormSecondaryAction type="button" onClick={handleEditAssignmentCancel}>
                Cancel
              </AdminFormSecondaryAction>
              <AdminHeaderAction onClick={handleEditAssignmentSave}
                disabled={!editSelectedRoleId || editAccessModules.length === 0}>Save</AdminHeaderAction>
            </div>
          </div>
          </>
        ) : isEditAssignmentPage ? (
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <AdminSupportingText>Assignment not found.</AdminSupportingText>
            <AdminLinkAction type="button" onClick={() => navigate('/admin/settings/rbac')} className="mt-2">
              Back to Roles & Access
            </AdminLinkAction>
          </DashboardMainContainer>
        ) : isAssignRolePage ? (
          <>
          <DashboardMainContainer className={`${ADMIN_FORM_PANEL_CLASS} overflow-x-auto`}>
            <AdminPanelTitle className="text-offblack">Assign Role</AdminPanelTitle>
            <div className="space-y-6">
              {!assignMemberId && (
                <AdminSupportingText>Open this screen from <span className="text-offblack font-medium">Team Members</span> → Assign Role on a member
                  without a role so the correct person is selected.</AdminSupportingText>
              )}
              <div className="space-y-2">
                <AdminDetailFieldLabel>Existing roles</AdminDetailFieldLabel>
                <div className="space-y-2">
                  {roles.map((r) => (
                    <label
                      key={r.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        assignSelectedRoleId === r.id ? 'border-primaryblue bg-primaryblue/5' : 'border-lightgray hover:border-primaryblue/50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="assignRole"
                        value={r.id}
                        checked={assignSelectedRoleId === r.id}
                        onChange={() => setAssignSelectedRoleId(r.id)}
                        className="mt-1 rounded-full border-lightgray text-primaryblue focus:ring-primaryblue"
                      />
                      <div className="min-w-0 flex-1">
                        <AdminTableHeader>{r.name}</AdminTableHeader>
                        <span className="text-mediumgray text-xs block mt-0.5">{r.description}</span>
                        <span className="text-offblack text-xs block mt-1">
                          {r.permissions?.includes(ALL_MODULES_LABEL) ? ALL_MODULES_LABEL : (r.permissions || []).join(', ')}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </DashboardMainContainer>
          <div className={ADMIN_FORM_FOOTER_CLASS}>
            <div className="flex flex-wrap items-center justify-end gap-3">
              <AdminFormSecondaryAction type="button" onClick={handleAssignCancel}>
                Cancel
              </AdminFormSecondaryAction>
              <AdminHeaderAction onClick={handleAssignSave}
                disabled={!assignMemberId || !assignSelectedRoleId}>Save</AdminHeaderAction>
            </div>
          </div>
          </>
        ) : isAssignmentsListPage ? (
          <>
            <AdminPageTitle>Role assignments</AdminPageTitle>
            <AdminSupportingText className="mb-8 px-4">Active links between team members and roles. Manage standard roles on{' '}
              <AdminLinkAction to="/admin/settings/rbac">
                Roles &amp; Access
              </AdminLinkAction>
              .</AdminSupportingText>
            <div className={ADMIN_TOOLBAR_CLASS}>
              <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end">
                <AdminFormSecondaryAction type="button" onClick={() => navigate('/admin/settings/rbac')}>
                  Role library
                </AdminFormSecondaryAction>
                <AdminHeaderAction type="button" onClick={() => navigate('/admin/settings/rbac/assign')}>
                  Assign Role
                </AdminHeaderAction>
              </div>
            </div>
          <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
            <div className="overflow-x-auto overflow-y-visible">
            <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4 min-w-[800px]" style={{ gridTemplateColumns: gridCols }}>
            <div className="flex items-center">
              <AdminTableHeader>Team Member</AdminTableHeader>
            </div>
            <div className="flex items-center">
              <AdminTableHeader>Permission</AdminTableHeader>
            </div>
            <div className="flex items-center">
              <AdminTableHeader>Status</AdminTableHeader>
            </div>
            <div className="flex items-center">
              <AdminTableHeader>Assigned On</AdminTableHeader>
            </div>
            <div className="flex items-center">
              <AdminTableHeader>Last Updated</AdminTableHeader>
            </div>
            <div className="flex items-center overflow-visible">
              <AdminTableHeader>Actions</AdminTableHeader>
            </div>
          </div>

          {assignments.length === 0 ? (
            <div className="py-10 px-4 text-center min-w-[800px]">
              <AdminSupportingText>No role assignments. Use Assign Role to add one.</AdminSupportingText>
            </div>
          ) : (
            assignments.map((row) => (
              <div
                key={row.id}
                className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4 min-w-[800px]"
                style={{ gridTemplateColumns: gridCols }}
              >
                <div className="flex items-center min-w-0">
                  <span className="text-offblack truncate" title={row.teamMemberName}>
                    {row.teamMemberName}
                  </span>
                </div>
                <div className="flex items-center min-w-0">
                  <span className="text-offblack truncate" title={permissionDisplay(row)}>
                    {permissionDisplay(row)}
                  </span>
                </div>
                <div className="flex items-center">
                  <AdminStatusBadge status={row.status} label={row.status} />
                </div>
                <div className="flex items-center min-w-0">
                  <span className="text-offblack">{formatDate(row.assignedOn)}</span>
                </div>
                <div className="flex items-center min-w-0">
                  <span className="text-offblack">{formatDate(row.lastUpdated)}</span>
                </div>
                <div className="flex items-center min-w-0 shrink-0 overflow-visible relative" onClick={(e) => e.stopPropagation()}>
                  <div
                    className="relative inline-flex"
                    ref={(el) => {
                      rowActionsRefs.current[row.id] = el;
                    }}
                  >
                    <AdminRowActionsButton
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleRowActions(row.id);
                      }}
                      title="Actions"
                      aria-label="Row actions"
                    >
                      <AdminRowMenuIcon />
                    </AdminRowActionsButton>
                  </div>
                </div>
              </div>
            ))
          )}
            </div>
          </DashboardMainContainer>
          {openRowId && rowMenuPosition && (() => {
            const openRow = assignments.find((a) => a.id === openRowId);
            if (!openRow) return null;
            return createPortal(
              <div
                ref={rowMenuDropdownRef}
                className={`fixed bg-white rounded-lg min-w-[200px] w-max max-w-[16rem] shadow-lg border border-lightgray z-[9999] py-2 ${
                  rowMenuPlacementReady ? 'visible' : 'invisible pointer-events-none'
                }`}
                style={{ top: rowMenuPosition.top, left: rowMenuPosition.left }}
              >
                {openRow.isOwner ? (
                  <div className="px-3 py-2">
                    <AdminSupportingText>View only (Owner)</AdminSupportingText>
                  </div>
                ) : (
                  <>
                    <AdminRowMenuItem
                      onClick={() => {
                        navigate(`/admin/settings/rbac/assignment/${openRow.id}/edit`);
                        setRowActionsOpen((prev) => ({ ...prev, [openRow.id]: false }));
                      }}
                    >
                      Edit Permission
                    </AdminRowMenuItem>
                    {openRow.status === 'Active' ? (
                      <AdminRowMenuItem
                        onClick={(e) => {
                          e.preventDefault();
                          setDeactivateModalOpen(openRow.id);
                          setRowActionsOpen((prev) => ({ ...prev, [openRow.id]: false }));
                        }}
                      >
                        Deactivate
                      </AdminRowMenuItem>
                    ) : (
                      <AdminRowMenuItem
                        onClick={(e) => {
                          e.preventDefault();
                          handleReactivate(openRow);
                          setRowActionsOpen((prev) => ({ ...prev, [openRow.id]: false }));
                        }}
                      >
                        Reactivate
                      </AdminRowMenuItem>
                    )}
                    <AdminRowMenuItem
                      className="text-alertred"
                      onClick={(e) => {
                        e.preventDefault();
                        setRemoveModalOpen(openRow.id);
                        setRowActionsOpen((prev) => ({ ...prev, [openRow.id]: false }));
                      }}
                    >
                      Remove Role
                    </AdminRowMenuItem>
                  </>
                )}
              </div>,
              document.body
            );
          })()}
          </>
        ) : null}
      </div>

      {/* Deactivate confirmation */}
      {deactivateModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setDeactivateModalOpen(null)}
          role="presentation"
        >
          <div
            className="bg-white rounded-xl shadow-lg border border-lightgray p-6 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <AdminPanelTitle className="text-offblack">Deactivate role access</AdminPanelTitle>
            <AdminSupportingText>This will disable login and platform access for this team member.</AdminSupportingText>
            <div className="flex gap-3 justify-end">
              <AdminHeaderAction onClick={() => setDeactivateModalOpen(null)}>Cancel</AdminHeaderAction>
              <AdminHeaderAction onClick={handleDeactivateConfirm}>Deactivate</AdminHeaderAction>
            </div>
          </div>
        </div>
      )}

      {/* Remove Role confirmation */}
      {removeModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setRemoveModalOpen(null)}
          role="presentation"
        >
          <div
            className="bg-white rounded-xl shadow-lg border border-lightgray p-6 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <AdminPanelTitle className="text-offblack">Remove role</AdminPanelTitle>
            <AdminSupportingText>This will remove role assignment. The user will have no access.</AdminSupportingText>
            <div className="flex gap-3 justify-end">
              <AdminHeaderAction onClick={() => setRemoveModalOpen(null)}>Cancel</AdminHeaderAction>
              <AdminHeaderAction onClick={handleRemoveConfirm}>Remove Role</AdminHeaderAction>
            </div>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  );
};

export default RolesPermissions;
